import React, { useRef } from 'react';
import { useDrag, useDrop } from 'react-dnd';
import { ListItem } from './ManualSort';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';

const ITEM_TYPE = 'LIST_ITEM';

interface DragItem {
  index: number;
  id: string;
  type: string;
}

interface DraggableItemProps {
  item: ListItem;
  index: number;
  moveItem: (from: number, to: number) => void;
  color?: string;
}

/**
 *
 * @returns a list item including drag and drop functionality
 */
const RenderList: React.FC<DraggableItemProps> = ({ item, index, moveItem, color }) => {
  const ref = useRef<HTMLLIElement>(null);

  const [, drop] = useDrop<DragItem>({
    accept: ITEM_TYPE,
    hover(draggedItem) {
      if (!ref.current) return;
      if (draggedItem.index === index) return;

      moveItem(draggedItem.index, index);
      draggedItem.index = index;
    },
  });

  const [{ isDragging }, drag] = useDrag({
    type: ITEM_TYPE,
    item: { id: item.id, index },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  drag(drop(ref));

  const isDarkMode =
    color?.toLowerCase() === 'white' ||
    color?.toLowerCase() === '#fff' ||
    color?.toLowerCase() === '#ffffff';

  return (
    <li
      ref={ref}
      style={{
        gap: '8px',
        padding: '2px 12px',
        marginBottom: '6px',
        textAlign: 'left',
        background: isDragging
          ? isDarkMode
            ? 'rgba(43, 185, 187, 0.3)'
            : '#e3f2fd'
          : isDarkMode
          ? 'rgba(255, 255, 255, 0.05)'
          : '#f8f9fa',
        border: '1px solid',
        borderColor: isDragging ? '#2bb9bb' : isDarkMode ? 'rgba(255, 255, 255, 0.2)' : '#e0e0e0',
        borderRadius: '4px',
        cursor: 'move',
        opacity: isDragging ? 0.6 : 1,
        transition: 'all 0.2s ease',
        boxShadow: isDragging ? '0 2px 8px rgba(43, 185, 187, 0.3)' : 'none',
        color: color || 'inherit',
      }}
    >
      <span style={{ display: 'flex' }}>
        <DragIndicatorIcon
          style={{
            marginLeft: '-3px',
            color: color ? color : 'rgba(113, 112, 112, 1)',
            opacity: 0.7,
          }}
        />
        <span
          style={{
            flex: 1,
            fontSize: '13px',
            userSelect: 'none',
          }}
        >
          {item.text}
        </span>
      </span>
    </li>
  );
};

export default RenderList;
